// Core UI primitives — buttons, cards, inputs, badges, skeletons.

import { forwardRef } from 'react';
import clsx from 'clsx';

export const Button = forwardRef(function Button({ variant = 'primary', size = 'md', loading, children, className, ...props }, ref) {
  const base = 'inline-flex items-center justify-center font-medium rounded-btn transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed';
  const variants = {
    primary: 'text-white bg-gradient-to-br from-primary-700 to-accent-600 shadow-soft hover:shadow-glow hover:-translate-y-px',
    secondary: 'bg-white text-ink-700 border border-ink-200 shadow-soft hover:bg-ink-50 hover:border-ink-300',
    ghost: 'text-ink-600 hover:bg-ink-100 hover:text-ink-900',
    danger: 'text-white bg-danger hover:bg-red-700 shadow-soft'
  };
  const sizes = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base'
  };
  return (
    <button ref={ref} className={clsx(base, variants[variant], sizes[size], className)} disabled={loading || props.disabled} {...props}>
      {loading ? <span className="inline-block w-3.5 h-3.5 mr-2 border-2 border-current border-r-transparent rounded-full animate-spin" /> : null}
      {children}
    </button>
  );
});

export function Card({ children, className, padding = true, ...props }) {
  return (
    <div className={clsx('bg-white rounded-card shadow-soft border border-ink-200/60', padding && 'p-6', className)} {...props}>
      {children}
    </div>
  );
}

export function CardHeader({ title, subtitle, action, className }) {
  return (
    <div className={clsx('flex items-start justify-between gap-4 mb-4', className)}>
      <div>
        <h3 className="text-lg font-semibold text-ink-900">{title}</h3>
        {subtitle ? <p className="text-sm text-ink-500 mt-0.5">{subtitle}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function Input({ label, error, hint, className, ...props }) {
  return (
    <div className={className}>
      {label ? <label className="label">{label}</label> : null}
      <input className={clsx('input', error && 'border-danger focus:border-danger focus:ring-danger/20')} {...props} />
      {error ? <p className="text-xs text-danger mt-1">{error}</p> : hint ? <p className="text-xs text-ink-500 mt-1">{hint}</p> : null}
    </div>
  );
}

export function Textarea({ label, error, hint, className, ...props }) {
  return (
    <div className={className}>
      {label ? <label className="label">{label}</label> : null}
      <textarea className={clsx('input min-h-[120px] py-2', error && 'border-danger')} {...props} />
      {error ? <p className="text-xs text-danger mt-1">{error}</p> : hint ? <p className="text-xs text-ink-500 mt-1">{hint}</p> : null}
    </div>
  );
}

export function Select({ label, error, options, className, ...props }) {
  return (
    <div className={className}>
      {label ? <label className="label">{label}</label> : null}
      <select className={clsx('input pr-10', error && 'border-danger')} {...props}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {error ? <p className="text-xs text-danger mt-1">{error}</p> : null}
    </div>
  );
}

export function Badge({ variant = 'grey', children, className }) {
  const map = {
    blue: 'bg-primary-100 text-primary-800',
    purple: 'bg-accent-100 text-accent-800',
    green: 'bg-success-soft text-green-800',
    amber: 'bg-warning-soft text-amber-800',
    red: 'bg-danger-soft text-red-800',
    grey: 'bg-ink-100 text-ink-700'
  };
  return <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', map[variant], className)}>{children}</span>;
}

export function Skeleton({ className }) {
  return <div className={clsx('skeleton', className)} />;
}

export function EmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="text-center py-12 px-4">
      {Icon ? <div className="mx-auto w-12 h-12 rounded-full bg-gradient-to-br from-primary-100 to-accent-100 flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-primary-700" />
      </div> : null}
      <h3 className="text-lg font-semibold text-ink-900">{title}</h3>
      {description ? <p className="text-sm text-ink-500 max-w-md mx-auto mt-2">{description}</p> : null}
      {action ? <div className="mt-6">{action}</div> : null}
    </div>
  );
}

export function ScoreBadge({ score, large }) {
  let variant = 'grey';
  if (score >= 75) variant = 'green';
  else if (score >= 60) variant = 'blue';
  else if (score >= 40) variant = 'amber';
  else if (score < 40) variant = 'red';
  const map = {
    grey: 'bg-ink-100 text-ink-700',
    green: 'bg-success-soft text-green-800',
    blue: 'bg-primary-100 text-primary-800',
    amber: 'bg-warning-soft text-amber-800',
    red: 'bg-danger-soft text-red-800'
  };
  return (
    <div className={clsx('inline-flex items-center justify-center font-semibold rounded-lg', map[variant], large ? 'w-16 h-16 text-2xl' : 'px-2.5 py-0.5 text-sm')}>
      {Math.round(score)}
    </div>
  );
}

export function PageHeader({ title, description, action }) {
  return (
    <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
      <div>
        <h1 className="text-2xl font-semibold text-ink-900 tracking-tight">{title}</h1>
        {description ? <p className="text-sm text-ink-500 mt-1 max-w-2xl">{description}</p> : null}
      </div>
      {action ? <div>{action}</div> : null}
    </div>
  );
}

export function Spinner({ className = 'w-5 h-5' }) {
  return <div className={clsx('inline-block border-2 border-current border-r-transparent rounded-full animate-spin text-ink-400', className)} />;
}
